import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { activeEntrySelector } from 'apps/formsCreationKit/store/selectors/entities'
import { activeStyledElementSelector } from 'apps/studio/store/selectors/pdf'
import { plgDemoSelector } from 'apps/studio/store/selectors/studioSettings'
import { filterSettingsForPlg } from 'apps/studio/components/settings/fields/plgSettingsFilter'
import { FIELD_SETTINGS_PANEL } from 'apps/studio/components/settings/fields/Base/standard'
import {
  TextSettings,
  LongTextSettings,
} from 'apps/studio/components/settings/fields/Text'
import {
  NumberSettings,
  IncrementSettings,
  SliderSettings,
  CurrencySettings,
} from 'apps/studio/components/settings/fields/Number'
import { DateSettings } from 'apps/studio/components/settings/fields/Date'
import { TimeSettings } from 'apps/studio/components/settings/fields/Time'
import { AttachmentSettings } from 'apps/studio/components/settings/fields/Attachment'
import { CheckboxSettings } from 'apps/studio/components/settings/fields/Checkbox'
import { StaticTextSettings } from 'apps/studio/components/settings/fields/StaticText'
import { PhotoSettings } from 'apps/studio/components/settings/fields/Photo'
import { SignatureSettings } from 'apps/studio/components/settings/fields/Signature'
import {
  CalculationSettings,
  SumSettings,
  CalcCurrencySettings,
} from 'apps/studio/components/settings/fields/Calculation'
import { DrawingSettings } from 'apps/studio/components/settings/fields/Drawing'
import { GPSSettings } from 'apps/studio/components/settings/fields/GPS'
import {
  LinkSettings,
  ButtonLinkSettings,
} from 'apps/studio/components/settings/fields/Link'
import { PaymentSettings } from 'apps/studio/components/settings/fields/Payment'
import { MirrorSettings } from 'apps/studio/components/settings/fields/Mirror'
import { StaticImageSettings } from 'apps/studio/components/settings/fields/StaticImage'
import { VideoSettings } from 'apps/studio/components/settings/fields/Video'
import { BarcodeSettings } from 'apps/studio/components/settings/fields/Barcode'
import {
  SingleChoiceBoxesSettings,
  SingleChoiceRadioSettings,
} from 'apps/studio/components/settings/fields/SingleChoice'
import { SingleChoiceDropdownSettings } from 'apps/studio/components/settings/fields/Dropdown'
import { MultipleChoiceSettings } from 'apps/studio/components/settings/fields/MultipleChoice'
import { RatingSettings } from 'apps/studio/components/settings/fields/Rating'
import { PassFailSettings } from 'apps/studio/components/settings/fields/PassFail'
import { settingInputs } from 'apps/studio/components/settings/inputs'
import { updateEntry } from 'apps/formsCreationKit/store/actions/sharedActions'
import { elementsActions } from 'apps/studio/store/reducers/entities/elements'
const { updateElementStyles, updateElementPosition } = elementsActions
import { slugify } from 'apps/builder/lib/utils'

const studioFieldSettings = {
  Text: TextSettings,
  'Long Text': LongTextSettings,
  Number: NumberSettings,
  Increment: IncrementSettings,
  Slider: SliderSettings,
  Currency: CurrencySettings,
  CalcCurrency: CalcCurrencySettings,
  Date: DateSettings,
  Time: TimeSettings,
  Attachment: AttachmentSettings,
  Checkbox: CheckboxSettings,
  'Static Text': StaticTextSettings,
  Photo: PhotoSettings,
  Signature: SignatureSettings,
  Calculation: CalculationSettings,
  Sum: SumSettings,
  Drawing: DrawingSettings,
  GPS: GPSSettings,
  Link: LinkSettings,
  ButtonLink: ButtonLinkSettings,
  Payment: PaymentSettings,
  Mirror: MirrorSettings,
  'Static Image': StaticImageSettings,
  Video: VideoSettings,
  Barcode: BarcodeSettings,
  'Single Choice Boxes': SingleChoiceBoxesSettings,
  'Single Choice Radio': SingleChoiceRadioSettings,
  'Multiple Choice': MultipleChoiceSettings,
  Rating: RatingSettings,
  Default: TextSettings,
  'Pass/Fail': PassFailSettings,
  Dropdown: SingleChoiceDropdownSettings,
}

const fetchEntryType = entry => {
  if (entry.type === 'Number') {
    if (entry.style == 1) {
      return 'Increment'
    } else if (entry.style == 2) {
      return 'Slider'
    } else if (entry.style == 4) {
      return 'Currency'
    }
  }

  if (entry.type === 'Calculation') {
    if (entry.style == 4) {
      return 'CalcCurrency'
    }
  }

  if (entry.type == 'Link') {
    if (entry.style == 0) {
      return 'ButtonLink'
    } else {
      return 'Link'
    }
  }

  if (entry.type == 'Single Choice') {
    if (entry.style == 1) {
      return 'Single Choice Radio'
    } else if (entry.style == 5) {
      return 'Single Choice Boxes'
    }
  }

  return entry.type
}

const makeFieldSettingsGetter = settingsConfig => entry => {
  const entryType = fetchEntryType(entry)
  const settings = settingsConfig[entryType] || settingsConfig['Default']
  return settings
}

export const getFieldSettingsConfig =
  makeFieldSettingsGetter(studioFieldSettings)

const updateAction = (updateType, guid, data) => {
  if (updateType === 'elementStyles') {
    return updateElementStyles({ entry_guid: guid, ...data })
  } else if (updateType === 'elementPosition') {
    return updateElementPosition({ entry_guid: guid, ...data })
  } else {
    return updateEntry({ guid, ...data })
  }
}

const getData = ({ updateType, entry, element }) => {
  switch (updateType) {
    case 'elementStyles':
      return element.styles
    case 'elementPosition':
      return element
    case 'entry':
    default:
      return entry
  }
}

const FieldSetting = ({ config, entry, element }) => {
  const {
    settingType,
    updateType,
    label,
    showInput = () => true,
    labelLarge,
    ...restConfig
  } = config
  const { guid } = entry
  const SettingInput = settingInputs[settingType] || settingInputs['textInput']
  const settingId = `${slugify(label)}--${settingType || 'textInput'}--${guid}`
  const inputVisible = showInput(entry)
  const data = getData({ updateType, entry, element })

  if (!inputVisible) return null
  return (
    <SettingInput
      id={settingId}
      label={label}
      guid={guid}
      styleLevel="element"
      {...restConfig}
      data={data}
      portal={true}
      inputId={settingId}
      update={data => updateAction(updateType, guid, data)}
    />
  )
}

// TODO: We might benefit from standardizing the panel markup into it's
// own component now that we can see where the repeated markup occurs
export const FieldSettings = ({ activePanels }) => {
  const activeEntry = useSelector(activeEntrySelector) || {}
  const activeElement = useSelector(activeStyledElementSelector) || {}
  const plgDemo = useSelector(plgDemoSelector)
  const fieldSettingsPanels = useMemo(
    () => getFieldSettingsConfig(activeEntry),
    [activeEntry]
  )

  const visiblePanels = useMemo(() => {
    if (!plgDemo) return fieldSettingsPanels
    return fieldSettingsPanels.map(panel => {
      if (panel.label !== FIELD_SETTINGS_PANEL) return panel
      return {
        ...panel,
        settings: panel.settings ? filterSettingsForPlg(panel.settings) : panel.settings,
      }
    })
  }, [fieldSettingsPanels, plgDemo])

  return visiblePanels.map(panel => {
    const {
      label,
      component: Component,
      panelButtons: PanelButtons,
      settings = [],
      plgUpsell: PlgUpsell,
    } = panel

    if (!activePanels.includes(label)) return null

    const showUpsell = plgDemo && PlgUpsell

    return (
      <div
        id={`field-settings-panel--${slugify(label)}`}
        className="gcds--control-panel workspace-panel"
        key={`field-settings-panel-${label}`}
      >
        <div className="panel--info --workspace-panel--header">
          <div className="panel--title --workspace-panel--title">{label}</div>
          {!showUpsell && PanelButtons && (
            <div className="panel--controls workspace-panel--buttons">
              <PanelButtons entry={activeEntry} />
            </div>
          )}
        </div>
        <div className="panel--content workspace-panel--section field-settings-panel">
          {showUpsell ? (
            <PlgUpsell />
          ) : (
            <>
              {Component && <Component entry={activeEntry} />}
              {settings &&
                settings.map(setting => (
                  <FieldSetting
                    key={`${setting.label}-${activeEntry.guid}-${setting.property}`}
                    config={setting}
                    entry={activeEntry}
                    element={activeElement}
                  />
                ))}
            </>
          )}
        </div>
      </div>
    )
  })
}
